/**
 * The order of the landing page, top to bottom: an ordered array of sections.
 * To move a section, move its line; to drop one, delete its line; to hide one
 * under a condition, give its entry an isShown. Nothing else needs changing:
 * each section is its own small component in sections/, and none draws its
 * own band or assumes what comes before or after it.
 *
 * There are two orders: the current page's (LANDING_SECTIONS) and the
 * remodelled page's (LANDING_SECTIONS_V2, shown by the Grow setting landing.V2
 * or ?landing=v2; see landingVersion). Sections that exist in both (the hero,
 * gardens, price and end) are the same component, told which page it is on.
 *
 * The header above the first section and the footer below the last are
 * LandingPage's own.
 *
 * Who uses it: LandingPage.
 *
 * @module landingSections
 */

import type { LandingSectionEntry } from './landingTypes';
import { Hero } from './sections/Hero';
import { Warnings } from './sections/Warnings';
import { PlantDoctorSection } from './sections/PlantDoctorSection';
import { SeedPackets } from './sections/SeedPackets';
import { Share } from './sections/Share';
import { Gardens } from './sections/Gardens';
import { FullLife } from './sections/FullLife';
import { FridayNote } from './sections/FridayNote';
import { Price } from './sections/Price';
import { Faq } from './sections/Faq';
import { EndCta } from './sections/EndCta';
import { SeedPacketsV2 } from './sections/SeedPacketsV2';
import { Scrapbook } from './sections/Scrapbook';
import { PlantDoctorV2 } from './sections/PlantDoctorV2';
import { ShareV2 } from './sections/ShareV2';
import { WarningsV2 } from './sections/WarningsV2';

/** The sections in the order the page shows them. */
export const LANDING_SECTIONS: readonly LandingSectionEntry[] = [
  { id: 'hero', Component: Hero },
  // The newest public gardens; it draws nothing while none is public.
  { id: 'gardens', Component: Gardens },
  { id: 'warnings', Component: Warnings },
  { id: 'plantDoctor', Component: PlantDoctorSection },
  { id: 'packets', Component: SeedPackets },
  { id: 'share', Component: Share },
  { id: 'life', Component: FullLife },
  { id: 'note', Component: FridayNote },
  { id: 'price', Component: Price },
  { id: 'faq', Component: Faq },
  { id: 'end', Component: EndCta },
];

/** The remodelled page's sections in the order it shows them (no Friday's note, FAQ or full-life cards). */
export const LANDING_SECTIONS_V2: readonly LandingSectionEntry[] = [
  { id: 'hero', Component: Hero },
  { id: 'packets', Component: SeedPacketsV2 },
  { id: 'scrapbook', Component: Scrapbook },
  { id: 'plantDoctor', Component: PlantDoctorV2 },
  { id: 'share', Component: ShareV2 },
  { id: 'warnings', Component: WarningsV2 },
  // The newest public gardens; it draws nothing while none is public.
  { id: 'gardens', Component: Gardens },
  { id: 'price', Component: Price },
  { id: 'end', Component: EndCta },
];
